const DEFAULT_PROXY_BASE = '/apps/gifting';
const CAMPAIGN_CODE_STORAGE_KEY = 'gifting_campaign_code';

/** @typedef {'token' | 'code' | ''} GiftingAccessMode */

/** @typedef {{ name: string, firstName?: string, lastName?: string, email?: string, phone?: string, igHandle?: string }} GiftingInfluencer */
/** @typedef {{ id: number, label: string, required?: boolean }} GiftingDeliverable */
/** @typedef {{ id: number, name: string, minItemAllowance: number, maxItemAllowance: number, contentDeadline?: string, contentDeadlineNote?: string, briefHtml?: string, agreementHtml?: string, termsHtml?: string, deliverables?: GiftingDeliverable[] }} GiftingCampaign */
/** @typedef {{ sizeId: number, name: string, stockAvailable: number }} GiftingSize */
/** @typedef {{ lineId: number, dropId?: number, productId: number, scheduleItemId: number, isPendingProduct: boolean, name: string, sku?: string, thumbUrl?: string, dropName?: string, goLiveDate?: string, expectedShipFrom?: string, maxQtyPerLine?: number | null, totalStock?: number, stockBadge?: string, isAvailable?: boolean, sortOrder?: number, dropSortOrder?: number, listIndex?: number, sizes: GiftingSize[] }} GiftingProduct */
/** @typedef {{ dropId: number, name: string, expectedShipFrom?: string, goLiveDate?: string, sortOrder?: number }} GiftingDrop */
/** @typedef {{ assetId: number, assetType: string, title?: string, url?: string, urlUs?: string, isHero?: boolean, sortOrder?: number }} GiftingAsset */
/** @typedef {{ influencer: GiftingInfluencer, campaign: GiftingCampaign, products: GiftingProduct[], assets: GiftingAsset[], drops?: GiftingDrop[], status: string, canSubmit: boolean, requiresIdentity?: boolean, sessionToken?: string }} GiftingSession */

/** @param {Record<string, unknown> | null | undefined} obj @param {...string} keys */
function pick(obj, ...keys) {
  if (!obj) return undefined;

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }

  return undefined;
}

/** @param {unknown} value @param {boolean} [defaultValue] */
function normalizeBoolean(value, defaultValue = false) {
  if (value === true || value === 1 || value === '1' || value === 'true' || value === 'True') return true;
  if (value === false || value === 0 || value === '0' || value === 'false' || value === 'False') return false;
  if (value === undefined || value === null || value === '') return defaultValue;
  return Boolean(value);
}

/** @param {unknown} data */
function isSessionPayloadValid(data) {
  const sessionRaw = pick(data, 'Session', 'session');
  const hasSession = Boolean(sessionRaw && typeof sessionRaw === 'object');
  const outcome = normalizeSessionStatus(pick(data, 'Outcome', 'outcome'));

  if (outcome === 'valid' || outcome === 'success') {
    return hasSession;
  }

  if (
    outcome === 'invalid' ||
    outcome === 'error' ||
    outcome === 'failed' ||
    outcome === 'expired' ||
    outcome === 'pending'
  ) {
    return false;
  }

  const validFlag = pick(data, 'Valid', 'valid');
  if (validFlag !== undefined && validFlag !== null && validFlag !== '') {
    return normalizeBoolean(validFlag, false) && hasSession;
  }

  return normalizeBoolean(pick(data, 'Ok', 'ok'), false) && hasSession;
}

/** @param {string} message */
function getInviteErrorPresentation(message) {
  const text = String(message || '').trim();
  const lower = text.toLowerCase();

  if (lower.includes('not been sent') || lower.includes('not sent yet')) {
    return {
      title: 'Invite not sent yet',
      message:
        text ||
        'This invite has not been sent yet. Ask the Sabo team to send your invite from the campaign admin before using this link.',
    };
  }

  if (lower.includes('expired')) {
    return {
      title: 'This link has expired',
      message: text || 'This invite link has expired. Contact the Sabo team for a new link.',
    };
  }

  if (lower.includes('declined')) {
    return {
      title: 'Invite unavailable',
      message: text || 'This gifting invite is no longer available.',
    };
  }

  return {
    title: 'Invalid invite link',
    message: text || 'This invite is not available.',
  };
}

/** @param {unknown} value */
function normalizeSessionStatus(value) {
  return String(value || '').trim().toLowerCase();
}

/** @param {Record<string, unknown>} record */
function resolveSessionStatus(record) {
  const influencerRaw = pick(record, 'Influencer', 'influencer');
  const influencer =
    influencerRaw && typeof influencerRaw === 'object'
      ? /** @type {Record<string, unknown>} */ (influencerRaw)
      : {};

  /** @type {string[]} */
  const candidates = [
    pick(record, 'Status', 'status'),
    pick(record, 'InfluencerStatus', 'influencerStatus'),
    pick(influencer, 'Status', 'status'),
  ]
    .map(normalizeSessionStatus)
    .filter(Boolean);

  const activeStatus = candidates.find(
    (status) => status === 'invited' || status === 'opened' || status === 'pending'
  );
  if (activeStatus) return activeStatus;

  return candidates[0] || '';
}

/** @param {GiftingSession} session */
function shouldShowGiftingWizard(session) {
  const status = normalizeSessionStatus(session.status);

  if (status === 'declined' || status === 'submitted') return false;

  if (isTokenAccessMode() || isCodeAccessMode()) {
    return true;
  }

  if (normalizeBoolean(session.canSubmit, true)) return true;
  if (status === 'invited' || status === 'opened') return true;

  if ((status === 'approved' || status === 'pending') && (session.products?.length || 0) > 0) {
    return true;
  }

  return false;
}

/** @param {unknown} value */
function normalizeDeliverables(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;

      const item = /** @type {Record<string, unknown>} */ (entry);
      const id = Number(pick(item, 'DeliverableId', 'deliverableId', 'Id', 'id') || 0);
      const label = String(pick(item, 'Label', 'label') || '').trim();

      if (!id || !label) return null;

      return {
        id,
        label,
        required: Boolean(pick(item, 'IsRequired', 'isRequired', 'Required', 'required')),
      };
    })
    .filter(Boolean);
}

/** @param {unknown} value @param {number} [fallback] */
function normalizeSortOrder(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** @param {Pick<GiftingProduct, 'sortOrder' | 'listIndex'>} a @param {Pick<GiftingProduct, 'sortOrder' | 'listIndex'>} b */
function compareBySortOrder(a, b) {
  const sortDiff = normalizeSortOrder(a.sortOrder) - normalizeSortOrder(b.sortOrder);
  if (sortDiff !== 0) return sortDiff;
  return normalizeSortOrder(a.listIndex) - normalizeSortOrder(b.listIndex);
}

/** @param {unknown} value */
function normalizeProducts(value) {
  if (!Array.isArray(value)) return [];

  return value.map((entry, index) => {
    const item = entry && typeof entry === 'object' ? /** @type {Record<string, unknown>} */ (entry) : {};
    const sizesRaw = pick(item, 'Sizes', 'sizes');
    const sizes = Array.isArray(sizesRaw)
      ? sizesRaw
          .map((sizeEntry) => {
            if (!sizeEntry || typeof sizeEntry !== 'object') return null;

            const size = /** @type {Record<string, unknown>} */ (sizeEntry);

            return {
              sizeId: Number(pick(size, 'SizeId', 'sizeId') || 0),
              name: String(pick(size, 'Name', 'name') || ''),
              stockAvailable: Number(pick(size, 'StockAvailable', 'stockAvailable') || 0),
            };
          })
          .filter(Boolean)
      : [];

    const apiLineId = Number(pick(item, 'LineId', 'lineId') || 0);
    const productId = Number(pick(item, 'ProductId', 'productId') || 0);
    const scheduleItemId = Number(pick(item, 'ScheduleItemId', 'scheduleItemId') || 0);

    return {
      // Always use a unique client id — API LineId can repeat across products.
      lineId: index + 1,
      apiLineId,
      dropId: Number(pick(item, 'DropId', 'dropId') || 0) || undefined,
      productId,
      scheduleItemId,
      isPendingProduct: Boolean(pick(item, 'IsPendingProduct', 'isPendingProduct')),
      name: String(pick(item, 'Name', 'name') || ''),
      sku: String(pick(item, 'Sku', 'sku') || ''),
      thumbUrl: String(pick(item, 'ThumbUrl', 'thumbUrl') || ''),
      dropName: String(pick(item, 'DropName', 'dropName') || ''),
      goLiveDate: String(pick(item, 'GoLiveDate', 'goLiveDate') || ''),
      expectedShipFrom: String(pick(item, 'ExpectedShipFrom', 'expectedShipFrom') || ''),
      maxQtyPerLine: pick(item, 'MaxQtyPerLine', 'maxQtyPerLine'),
      totalStock: Number(pick(item, 'TotalStock', 'totalStock') || 0),
      stockBadge: String(pick(item, 'StockBadge', 'stockBadge') || ''),
      isAvailable: pick(item, 'IsAvailable', 'isAvailable') !== false,
      sortOrder: normalizeSortOrder(pick(item, 'SortOrder', 'sortOrder')),
      dropSortOrder: normalizeSortOrder(pick(item, 'DropSortOrder', 'dropSortOrder')),
      listIndex: index,
      sizes: /** @type {GiftingSize[]} */ (sizes),
    };
  });
}

/** @param {unknown} value */
function normalizeDrops(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;

      const item = /** @type {Record<string, unknown>} */ (entry);
      const dropId = Number(pick(item, 'DropId', 'dropId') || 0);

      if (!dropId) return null;

      return {
        dropId,
        name: String(pick(item, 'Name', 'name') || ''),
        expectedShipFrom: String(pick(item, 'ExpectedShipFrom', 'expectedShipFrom') || ''),
        goLiveDate: String(pick(item, 'GoLiveDate', 'goLiveDate') || ''),
        sortOrder: normalizeSortOrder(pick(item, 'SortOrder', 'sortOrder')),
      };
    })
    .filter(Boolean);
}

/** @param {unknown} value */
function normalizeAssets(value) {
  if (!Array.isArray(value)) return [];

  return value.map((entry) => {
    const item = entry && typeof entry === 'object' ? /** @type {Record<string, unknown>} */ (entry) : {};

    return {
      assetId: Number(pick(item, 'AssetId', 'assetId') || 0),
      assetType: String(pick(item, 'AssetType', 'assetType') || ''),
      title: String(pick(item, 'Title', 'title') || ''),
      url: String(pick(item, 'Url', 'url') || ''),
      urlUs: String(pick(item, 'UrlUs', 'urlUs') || ''),
      isHero: Boolean(pick(item, 'IsHero', 'isHero')),
      sortOrder: Number(pick(item, 'SortOrder', 'sortOrder') || 0),
    };
  });
}

/** @param {unknown} raw */
function normalizeSession(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const record = /** @type {Record<string, unknown>} */ (raw);
  const campaignRaw = pick(record, 'Campaign', 'campaign');
  const influencerRaw = pick(record, 'Influencer', 'influencer');
  const campaign = campaignRaw && typeof campaignRaw === 'object' ? /** @type {Record<string, unknown>} */ (campaignRaw) : {};
  const influencer = influencerRaw && typeof influencerRaw === 'object' ? /** @type {Record<string, unknown>} */ (influencerRaw) : {};
  const deliverables = normalizeDeliverables(pick(campaign, 'Deliverables', 'deliverables'));

  return {
    influencer: {
      name: String(pick(influencer, 'Name', 'name') || ''),
      firstName: String(pick(influencer, 'FirstName', 'firstName') || ''),
      lastName: String(pick(influencer, 'LastName', 'lastName') || ''),
      email: String(pick(influencer, 'Email', 'email') || ''),
      phone: String(pick(influencer, 'Phone', 'phone') || ''),
      igHandle: String(pick(influencer, 'IgHandle', 'igHandle') || ''),
    },
    campaign: {
      id: Number(pick(campaign, 'Id', 'id') || 0),
      name: String(pick(campaign, 'Name', 'name') || ''),
      minItemAllowance: Number(pick(campaign, 'MinItemAllowance', 'minItemAllowance') || 0),
      maxItemAllowance: Number(pick(campaign, 'MaxItemAllowance', 'maxItemAllowance') || 0),
      contentDeadline: String(pick(campaign, 'ContentDeadline', 'contentDeadline') || ''),
      contentDeadlineNote: String(pick(campaign, 'ContentDeadlineNote', 'contentDeadlineNote') || ''),
      briefHtml: String(pick(campaign, 'BriefHtml', 'briefHtml') || ''),
      agreementHtml: String(pick(campaign, 'AgreementHtml', 'agreementHtml') || ''),
      termsHtml: String(pick(campaign, 'TermsHtml', 'termsHtml') || ''),
      deliverables,
    },
    drops: normalizeDrops(pick(record, 'Drops', 'drops')),
    products: normalizeProducts(pick(record, 'Products', 'products')),
    assets: normalizeAssets(pick(record, 'Assets', 'assets')),
    status: resolveSessionStatus(record),
    canSubmit: normalizeBoolean(pick(record, 'CanSubmit', 'canSubmit'), true),
    requiresIdentity: Boolean(pick(record, 'RequiresIdentity', 'requiresIdentity')),
    sessionToken: String(pick(record, 'Token', 'token') || ''),
  };
}

/** @param {GiftingProduct} product */
function getProductKey(product) {
  return String(product.lineId);
}

/** @param {{ sizeId?: number, qty?: number } | undefined} entry */
function isActiveSelection(entry) {
  if (!entry || Number(entry.qty) <= 0) return false;
  return Number.isFinite(Number(entry.sizeId));
}

/** @returns {boolean} */
function isUsShopifyStore() {
  const host = String(window.location.hostname || '').toLowerCase();
  if (host.includes('.com.au')) return false;
  if (host === 'saboskirt.com' || host === 'www.saboskirt.com' || host.includes('us.saboskirt.com')) {
    return true;
  }

  const shop = String(window.Shopify?.shop || '').toLowerCase();
  return shop.includes('.com') && !shop.includes('.com.au');
}

/** @param {GiftingAsset} asset */
function getAssetUrl(asset) {
  if (isUsShopifyStore() && asset.urlUs) return asset.urlUs;
  return asset.url || asset.urlUs || '';
}

/** @param {string | undefined} badge */
function isOutOfStockBadge(badge) {
  return String(badge || '').toLowerCase() === 'outofstock';
}

/** @param {string | undefined} badge */
function isLowStockBadge(badge) {
  return String(badge || '').toLowerCase() === 'low';
}

/** @type {GiftingSession | null} */
let sessionData = null;

/** @type {string} */
let inviteToken = '';

/** @type {string} */
let campaignCode = '';

/** @type {GiftingAccessMode} */
let accessMode = '';

/** @type {number} */
let currentStepIndex = 0;

/** @type {boolean} */
let isReadOnly = false;

/** @type {Record<string, { lineId: number, productId: number, scheduleItemId: number, isPendingProduct: boolean, sizeId: number, qty: number }>} */
let selections = {};

/** @type {{ firstName: string, lastName: string, email: string, phone: string, igHandle: string, addressLine1: string, city: string, postcode: string, country: string, notes: string, deadlineChoice: 'confirm' | 'need_time' | '', deliverableIds: number[], termsAccepted: boolean, dataConsentAccepted: boolean }} */
let formState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  igHandle: '',
  addressLine1: '',
  city: '',
  postcode: '',
  country: 'Australia',
  notes: '',
  deadlineChoice: '',
  deliverableIds: [],
  termsAccepted: false,
  dataConsentAccepted: false,
};

function getGiftingRoot() {
  const root = document.querySelector('[data-gifting-root]');
  return root instanceof HTMLElement ? root : null;
}

function isThemePreview() {
  return (
    window.location.hostname.includes('shopifypreview.com') ||
    Boolean(window.Shopify?.designMode) ||
    Boolean(window.Shopify?.visualPreviewMode)
  );
}

function getProxyBase() {
  const root = getGiftingRoot();
  const override = root ? String(root.dataset.apiBase || '').trim() : '';
  return override || DEFAULT_PROXY_BASE;
}

function resolvePreviewCampaignCode() {
  if (isReCollabLanding()) return '';

  if (!isThemePreview()) return '';

  const root = getGiftingRoot();
  return root ? String(root.dataset.previewCampaignCode || root.dataset.previewToken || '').trim() : '';
}

function isReCollabLanding() {
  const answer = (new URLSearchParams(window.location.search).get('answer') || '').trim().toLowerCase();
  return answer === 'yes' || answer === 'no';
}

function resolveInviteToken() {
  if (isReCollabLanding()) return '';

  const params = new URLSearchParams(window.location.search);
  return String(params.get('token') || '').trim();
}

function readStoredCampaignCode() {
  try {
    return String(sessionStorage.getItem(CAMPAIGN_CODE_STORAGE_KEY) || '').trim();
  } catch (_error) {
    return '';
  }
}

function persistCampaignCode(code) {
  campaignCode = String(code || '').trim();

  try {
    if (campaignCode) {
      sessionStorage.setItem(CAMPAIGN_CODE_STORAGE_KEY, campaignCode);
    } else {
      sessionStorage.removeItem(CAMPAIGN_CODE_STORAGE_KEY);
    }
  } catch (_error) {
    // Ignore storage failures.
  }
}

function clearStoredCampaignCode() {
  campaignCode = '';
  accessMode = '';

  try {
    sessionStorage.removeItem(CAMPAIGN_CODE_STORAGE_KEY);
  } catch (_error) {
    // Ignore storage failures.
  }
}

/** @type {boolean} */
let igHandleLocked = false;

const GIFTING_PRODUCTS_PAGE_SIZE = 12;

/** @type {Record<string, number>} */
let visibleCountByDrop = {};

/** @type {Record<string, boolean>} */
let collapsedDrops = {};

function resetProductListUiState() {
  visibleCountByDrop = {};
  collapsedDrops = {};
}

function isCodeAccessMode() {
  return accessMode === 'code';
}

function isTokenAccessMode() {
  return accessMode === 'token';
}

/** @param {GiftingSession | null | undefined} session */
function getActiveSteps(session) {
  if (isCodeAccessMode() || isTokenAccessMode() || session?.requiresIdentity) {
    return /** @type {const} */ (['products', 'details', 'terms']);
  }
  return /** @type {const} */ (['products', 'terms']);
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/** @param {string} html */
function sanitizeHtml(html) {
  const template = document.createElement('template');
  template.innerHTML = String(html ?? '');

  template.content.querySelectorAll('script, iframe, object, embed, link, meta, style').forEach((node) => {
    node.remove();
  });

  template.content.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on') || name === 'srcdoc') {
        node.removeAttribute(attr.name);
      }
    });
  });

  return template.innerHTML;
}

/** @param {'success' | 'error'} tone @param {string} title @param {string} message */
function showTerminal(tone, title, message) {
  hideViews(['terminal']);

  const circle = document.querySelector('[data-gifting-status-circle]');
  const titleEl = document.querySelector('[data-gifting-terminal-title]');
  const messageEl = document.querySelector('[data-gifting-terminal-message]');

  if (circle instanceof HTMLElement) {
    circle.className = `gifting-ambassador__status-circle gifting-ambassador__status-circle--${tone}`;
    circle.textContent = tone === 'success' ? '✓' : '✕';
  }

  if (titleEl instanceof HTMLElement) titleEl.textContent = title;
  if (messageEl instanceof HTMLElement) messageEl.textContent = message;
}

/** @param {Array<'loading' | 'unlock' | 'wizard' | 'terminal'>} visible */
function hideViews(visible) {
  const map = {
    loading: document.querySelector('[data-gifting-loading]'),
    unlock: document.querySelector('[data-gifting-unlock]'),
    wizard: document.querySelector('[data-gifting-wizard]'),
    terminal: document.querySelector('[data-gifting-terminal]'),
  };

  /** @type {Array<'loading' | 'unlock' | 'wizard' | 'terminal'>} */
  const keys = ['loading', 'unlock', 'wizard', 'terminal'];

  keys.forEach((key) => {
    const el = map[key];
    if (el instanceof HTMLElement) {
      el.hidden = !visible.includes(key);
    }
  });
}

function showLoading() {
  hideViews(['loading']);
}

function showUnlock() {
  hideViews(['unlock']);
  bindUnlockForm();
}

/** @param {string} message */
function setUnlockError(message) {
  const errorEl = document.querySelector('[data-gifting-unlock-error]');
  if (!(errorEl instanceof HTMLElement)) return;

  if (!message) {
    errorEl.hidden = true;
    errorEl.textContent = '';
    return;
  }

  errorEl.hidden = false;
  errorEl.textContent = message;
}

function bindUnlockForm() {
  const form = document.querySelector('[data-gifting-unlock-form]');
  if (!(form instanceof HTMLFormElement) || form.dataset.bound) return;

  form.dataset.bound = 'true';
  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const input = form.querySelector('[data-gifting-unlock-input]');
    const submitBtn = form.querySelector('[data-gifting-unlock-submit]');
    if (!(input instanceof HTMLInputElement) || !(submitBtn instanceof HTMLButtonElement)) return;

    const code = String(input.value || '').trim();
    if (!code) {
      setUnlockError('Please enter your campaign code.');
      return;
    }

    setUnlockError('');
    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-busy', 'true');
    showLoading();

    await unlockByCampaignCode(code, { fromUnlock: true });

    submitBtn.disabled = false;
    submitBtn.removeAttribute('aria-busy');
  });
}

function rememberInviteTokenInUrl() {
  if (isThemePreview() || accessMode !== 'token' || !inviteToken) return;

  const url = new URL(window.location.href);
  url.searchParams.delete('code');

  if (url.searchParams.get('token') === inviteToken) return;

  url.searchParams.set('token', inviteToken);
  history.replaceState({}, '', url);
}

/** @param {string} message @param {string} [title] */
function showError(message, title) {
  showTerminal(
    'error',
    title || 'Unable to load invite',
    message || 'The campaign has closed or this invite is no longer valid. Reach out to the Sabo team if you think this is a mistake.'
  );
}

/** @param {GiftingSession} session @param {string} [message] */
function showSubmitted(session, message) {
  const firstName = formState.firstName || session.influencer?.firstName || splitName(session.influencer?.name).firstName || 'there';
  showTerminal(
    'success',
    `You're all submitted, ${firstName}`,
    getSubmittedMessage(session, message)
  );
}

/** @param {string} name */
function splitName(name) {
  const parts = String(name || '').trim().split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || '',
  };
}

/** @param {string} token */
async function fetchSessionByToken(token) {
  const response = await fetch(`${getProxyBase()}/session?token=${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  return parseProxyResponse(response, 'Unable to load invite. Please open the link from your email.');
}

/** @param {string} code */
async function fetchSessionByCampaignCode(code) {
  const response = await fetch(`${getProxyBase()}/unlock-code`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    cache: 'no-store',
    body: JSON.stringify({ CampaignCode: code }),
  });

  return parseProxyResponse(response, 'Unable to unlock this campaign code right now.');
}

/** @param {Response} response @param {string} authErrorMessage */
async function parseProxyResponse(response, authErrorMessage) {
  if (response.status === 401) {
    throw new Error(authErrorMessage);
  }

  if (response.status === 404) {
    throw new Error(
      isThemePreview()
        ? 'Gifting API not found on preview. Confirm the gifting app proxy is installed on this store, then reload.'
        : 'Unable to load gifting right now. Please try again shortly.'
    );
  }

  if (!response.ok) {
    throw new Error('Unable to load gifting right now. Please try again shortly.');
  }

  return response.json();
}

/** @param {unknown} data @param {{ fromUnlock?: boolean, invalidMessage?: string }} [options] */
function handleSessionPayload(data, options = {}) {
  const valid = isSessionPayloadValid(data);
  const message = String(pick(data, 'Message', 'message') || '');
  const session = normalizeSession(pick(data, 'Session', 'session'));

  if (!valid || !session) {
    if (options.fromUnlock) {
      showUnlock();
      setUnlockError(message || options.invalidMessage || 'That campaign code is not valid. Check your code and try again.');
    } else {
      const presentation = getInviteErrorPresentation(message);
      showError(presentation.message, presentation.title);
    }
    return null;
  }

  if (session.sessionToken && accessMode === 'token') {
    inviteToken = session.sessionToken;
  }

  if (!shouldShowGiftingWizard(session) && normalizeSessionStatus(session.status) === 'submitted') {
    showSubmitted(session);
    return null;
  }

  if (accessMode === 'token') {
    rememberInviteTokenInUrl();
  }

  renderSession(session);
  return session;
}

/** @returns {GiftingDeliverable[]} */
function getDeliverables(session) {
  return session.campaign?.deliverables || [];
}

/** @param {GiftingSession} session */
function initFormState(session) {
  const influencer = session.influencer || {};
  const split = splitName(influencer.name);

  formState = {
    firstName: influencer.firstName || split.firstName,
    lastName: influencer.lastName || split.lastName,
    email: influencer.email || '',
    phone: influencer.phone || '',
    igHandle: influencer.igHandle || '',
    addressLine1: '',
    city: '',
    postcode: '',
    country: 'Australia',
    notes: '',
    deadlineChoice: '',
    deliverableIds: [],
    termsAccepted: false,
    dataConsentAccepted: false,
  };

  igHandleLocked = isTokenAccessMode() && Boolean(String(influencer.igHandle || '').trim());

  selections = {};
  resetProductListUiState();
}

/** @param {GiftingSession} session */
function applyReadOnlyStatus(session) {
  const status = normalizeSessionStatus(session.status);

  if (status === 'submitted') {
    isReadOnly = true;
    showSubmitted(session);
    return true;
  }

  if (status === 'declined') {
    isReadOnly = true;
    showError('This gifting invite is no longer available.');
    return true;
  }

  // Personal token links and campaign codes should unlock the picker unless truly finished.
  if (isTokenAccessMode() || isCodeAccessMode()) {
    return false;
  }

  if (shouldShowGiftingWizard(session)) {
    return false;
  }

  if (status === 'approved') {
    isReadOnly = true;
    showTerminal(
      'success',
      'Approved',
      "Approved — we'll be in touch with next steps."
    );
    return true;
  }

  if (status === 'pending') {
    isReadOnly = true;
    showError(
      'This invite has not been sent yet. Ask the Sabo team to send your invite from the campaign admin.',
      'Invite not sent yet'
    );
    return true;
  }

  isReadOnly = true;
  showError('This gifting invite is no longer available.');
  return true;
}

/** @returns {number} */
function getSelectedCount() {
  return Object.values(selections).filter((entry) => isActiveSelection(entry)).length;
}

/** @param {GiftingSession} session */
function renderSelectionCountHtml(session) {
  const selectedCount = getSelectedCount();
  const min = Number(session.campaign?.minItemAllowance || 0);
  const max = Number(session.campaign?.maxItemAllowance || 0);

  if (min <= 0 && max <= 0) return '';

  const hasMin = min > 0;
  const hasMax = max > 0;
  const exactCount = hasMin && hasMax && min === max;
  const belowMin = hasMin && selectedCount < min;
  const atMax = hasMax && selectedCount >= max;
  const atMin = !belowMin;
  const progressTarget = hasMin ? min : max;
  const progressPct =
    progressTarget > 0 ? Math.min(100, Math.round((selectedCount / progressTarget) * 100)) : 0;

  /** @type {'warning' | 'complete' | 'max'} */
  let state = 'warning';
  if (atMax) state = 'max';
  else if (atMin) state = 'complete';

  /** @type {string} */
  let countDisplay = '';
  /** @type {string} */
  let countLabel = `piece${selectedCount === 1 ? '' : 's'} selected`;
  /** @type {string} */
  let meta = '';
  /** @type {string} */
  let note = '';

  if (exactCount) {
    countDisplay = `${selectedCount}<span class="gifting-ambassador__selection-progress-sep">/</span>${min}`;
    if (belowMin) {
      note = `Select ${min - selectedCount} more (${min} required)`;
    } else {
      note = `${min} items selected — ready to continue`;
    }
  } else if (hasMax) {
    countDisplay = `${selectedCount}<span class="gifting-ambassador__selection-progress-sep">/</span>${max}`;
    if (belowMin && hasMin) {
      note = `Minimum ${min} · ${min - selectedCount} more needed`;
    } else if (atMax) {
      note = `Maximum of ${max} item${max === 1 ? '' : 's'} selected`;
    } else if (hasMin && atMin) {
      const remaining = max - selectedCount;
      note =
        remaining > 0
          ? `Minimum reached · up to ${remaining} more allowed`
          : 'Ready to continue';
    } else if (selectedCount === 0) {
      note = `Choose up to ${max} item${max === 1 ? '' : 's'}`;
    } else {
      note = `${max - selectedCount} more allowed`;
    }
  } else if (hasMin) {
    countDisplay = `${selectedCount}`;
    meta = `Min. ${min}`;
    if (belowMin) {
      note = `Select at least ${min - selectedCount} more to continue`;
    } else {
      note = 'Minimum reached — add more if you like; we\'ll confirm your final picks';
    }
  }

  let ariaLabel = `${selectedCount} item${selectedCount === 1 ? '' : 's'} selected`;
  if (exactCount) {
    ariaLabel = `${selectedCount} of ${min} required items selected`;
  } else if (hasMax) {
    ariaLabel = `${selectedCount} of ${max} items selected`;
    if (hasMin) ariaLabel += `, minimum ${min}`;
  } else if (hasMin) {
    ariaLabel = `${selectedCount} items selected, minimum ${min}`;
  }
  if (note) ariaLabel += `. ${note}`;

  return `
    <div
      class="gifting-ambassador__selection-progress gifting-ambassador__selection-progress--${state}"
      data-gifting-selection-count
      role="status"
      aria-live="polite"
      aria-label="${escapeHtml(ariaLabel)}"
    >
      <div class="gifting-ambassador__selection-progress-head">
        <span class="gifting-ambassador__selection-progress-count" aria-hidden="true">${countDisplay}</span>
        <span class="gifting-ambassador__selection-progress-label">${countLabel}</span>
        ${meta ? `<span class="gifting-ambassador__selection-progress-meta">${escapeHtml(meta)}</span>` : ''}
      </div>
      <div class="gifting-ambassador__selection-progress-track" aria-hidden="true">
        <span class="gifting-ambassador__selection-progress-fill" style="width: ${progressPct}%"></span>
      </div>
      ${note ? `<p class="gifting-ambassador__selection-progress-note">${escapeHtml(note)}</p>` : ''}
    </div>
  `;
}

/** @param {GiftingProduct} product @param {GiftingSession} session */
function getProductShipFromDisplay(product, session) {
  const direct = String(product.expectedShipFrom || '').trim();
  if (direct) return direct;

  const dropId = Number(product.dropId || 0);
  if (!dropId || !session.drops?.length) return '';

  const drop = session.drops.find((entry) => entry.dropId === dropId);
  return String(drop?.expectedShipFrom || '').trim();
}

/** @param {GiftingProduct[]} products @param {GiftingSession} session */
function groupProductsByDrop(products, session) {
  /** @type {Map<string, { key: string, dropName: string, shipFrom: string, sortOrder: number, listIndex: number, products: GiftingProduct[] }>} */
  const groups = new Map();

  for (const product of products) {
    const dropId = Number(product.dropId || 0);
    const drop =
      dropId && session.drops?.length
        ? session.drops.find((entry) => entry.dropId === dropId)
        : null;
    const dropName = String(product.dropName || drop?.name || '').trim() || 'Collection';
    const key = dropId ? `drop-${dropId}` : `drop-${dropName.toLowerCase().replace(/\s+/g, '-')}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        dropName,
        shipFrom: String(drop?.expectedShipFrom || product.expectedShipFrom || '').trim(),
        sortOrder: normalizeSortOrder(drop?.sortOrder ?? product.dropSortOrder),
        listIndex: normalizeSortOrder(product.listIndex),
        products: [],
      });
    }

    groups.get(key)?.products.push(product);
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      products: [...group.products].sort(compareBySortOrder),
    }))
    .sort((a, b) => {
      const sortDiff = normalizeSortOrder(a.sortOrder) - normalizeSortOrder(b.sortOrder);
      if (sortDiff !== 0) return sortDiff;
      return normalizeSortOrder(a.listIndex) - normalizeSortOrder(b.listIndex);
    });
}

/** @param {Array<{ key: string }>} groups */
function ensureProductListUiState(groups) {
  groups.forEach((group) => {
    if (visibleCountByDrop[group.key] === undefined) {
      visibleCountByDrop[group.key] = GIFTING_PRODUCTS_PAGE_SIZE;
    }
    if (collapsedDrops[group.key] === undefined) {
      collapsedDrops[group.key] = false;
    }
  });
}

/** @param {GiftingSession} session */
function renderSelectedSummaryHtml(session) {
  const items = getSelectedLineSummaries(session);
  if (!items.length) return '';

  const rows = items
    .map((item) => {
      const thumb = item.thumbUrl
        ? `<img class="gifting-ambassador__selected-summary-thumb" src="${escapeHtml(item.thumbUrl)}" alt="" loading="lazy" decoding="async">`
        : '<span class="gifting-ambassador__selected-summary-thumb gifting-ambassador__selected-summary-thumb--empty" aria-hidden="true"></span>';

      return `
        <li class="gifting-ambassador__selected-summary-item">
          ${thumb}
          <div class="gifting-ambassador__selected-summary-copy">
            <span class="gifting-ambassador__selected-summary-name">${escapeHtml(item.name)}</span>
            ${item.sizeName ? `<span class="gifting-ambassador__selected-summary-size">Size ${escapeHtml(item.sizeName)}</span>` : ''}
          </div>
        </li>
      `;
    })
    .join('');

  return `
    <div class="gifting-ambassador__selected-summary" data-gifting-selected-summary>
      <p class="gifting-ambassador__selected-summary-label">Your selections (${items.length})</p>
      <ul class="gifting-ambassador__selected-summary-list">${rows}</ul>
    </div>
  `;
}

/** @param {{ key: string, dropName: string, shipFrom: string, products: GiftingProduct[] }} group @param {GiftingSession} session */
function renderDropSection(group, session) {
  const visibleCount = visibleCountByDrop[group.key] ?? GIFTING_PRODUCTS_PAGE_SIZE;
  const collapsed = collapsedDrops[group.key] ?? false;
  const visibleProducts = group.products.slice(0, visibleCount);
  const remaining = group.products.length - visibleProducts.length;
  const loadMoreCount = Math.min(GIFTING_PRODUCTS_PAGE_SIZE, remaining);

  return `
    <section class="gifting-ambassador__drop${collapsed ? ' is-collapsed' : ''}" data-drop-key="${escapeHtml(group.key)}">
      <button
        type="button"
        class="gifting-ambassador__drop-toggle"
        data-gifting-drop-toggle
        data-drop-key="${escapeHtml(group.key)}"
        aria-expanded="${collapsed ? 'false' : 'true'}"
      >
        <span class="gifting-ambassador__drop-title">${escapeHtml(group.dropName)}</span>
        ${group.shipFrom ? `<span class="gifting-ambassador__drop-meta">Ships from ${escapeHtml(group.shipFrom)}</span>` : ''}
        ${
          remaining > 0
            ? `<span class="gifting-ambassador__drop-meta gifting-ambassador__drop-meta--truncated">${visibleProducts.length} of ${group.products.length} shown</span>`
            : `<span class="gifting-ambassador__drop-count">${group.products.length} piece${group.products.length === 1 ? '' : 's'}</span>`
        }
      </button>
      <div class="gifting-ambassador__drop-panel">
        <div class="gifting-ambassador__drop-panel-inner">
          <div class="gifting-ambassador__product-grid" data-gifting-product-grid>
            ${visibleProducts.map((product) => renderProductCard(product, session)).join('')}
          </div>
          ${
            remaining > 0
              ? `
                <div class="gifting-ambassador__load-more-block">
                  <p class="gifting-ambassador__load-more-meta">
                    Showing ${visibleProducts.length} of ${group.products.length} pieces in this drop
                  </p>
                  <button type="button" class="gifting-ambassador__load-more" data-gifting-load-more data-drop-key="${escapeHtml(group.key)}">
                    ${
                      remaining <= loadMoreCount
                        ? `View all ${remaining} remaining piece${remaining === 1 ? '' : 's'}`
                        : `View ${loadMoreCount} more piece${loadMoreCount === 1 ? '' : 's'} (${remaining} left)`
                    }
                  </button>
                </div>
              `
              : ''
          }
        </div>
      </div>
    </section>
  `;
}

/** @param {GiftingSession} session */
function getSelectedLineSummaries(session) {
  return Object.values(selections)
    .filter((entry) => isActiveSelection(entry))
    .map((entry) => {
      const product = session.products?.find((item) => item.lineId === entry.lineId);
      const size = product?.sizes?.find((item) => Number(item.sizeId) === Number(entry.sizeId));

      return {
        name: product?.name || 'Selected item',
        sizeName: size?.name || '',
        shipFrom: product ? getProductShipFromDisplay(product, session) : '',
        thumbUrl: product?.thumbUrl || '',
      };
    });
}

/** @param {GiftingSession} session */
function renderSubmitSummaryHtml(session) {
  const items = getSelectedLineSummaries(session);
  if (!items.length) return '';

  const rows = items
    .map((item) => {
      const metaParts = [];
      if (item.sizeName) metaParts.push(`Size ${item.sizeName}`);
      if (item.shipFrom) metaParts.push(`Ships from ${item.shipFrom}`);
      const meta = metaParts.length
        ? `<span class="gifting-ambassador__submit-summary-size">${metaParts.map((part) => escapeHtml(part)).join(' · ')}</span>`
        : '';

      return `
        <li class="gifting-ambassador__submit-summary-item">
          ${
            item.thumbUrl
              ? `<img class="gifting-ambassador__submit-summary-thumb" src="${escapeHtml(item.thumbUrl)}" alt="" loading="lazy" decoding="async">`
              : ''
          }
          <div class="gifting-ambassador__submit-summary-copy">
            <span class="gifting-ambassador__submit-summary-name">${escapeHtml(item.name)}</span>
            ${meta}
          </div>
        </li>
      `;
    })
    .join('');

  return `
    <div class="gifting-ambassador__submit-summary">
      <h3 class="gifting-ambassador__submit-summary-title">Your selections</h3>
      <ul class="gifting-ambassador__submit-summary-list">${rows}</ul>
    </div>
  `;
}

/** @param {GiftingSession} session @param {string} [apiMessage] */
function getSubmittedMessage(session, apiMessage) {
  if (apiMessage) return apiMessage;

  if (isCodeAccessMode()) {
    return "You're in! We've received your submission and it's pending approval. We'll review your selections and email you once confirmed.";
  }

  return "We're reviewing your picks and will email you once approved. Keep an eye on your inbox.";
}

/** @param {GiftingSession} session */
function renderProgress(session) {
  const progress = document.querySelector('[data-gifting-progress]');
  if (!(progress instanceof HTMLElement) || isReadOnly) return;

  progress.innerHTML = getActiveSteps(session).map((step, index) => {
    const classes = [
      'gifting-ambassador__progress-dot',
      index <= currentStepIndex ? 'is-active' : '',
      index < currentStepIndex ? 'is-complete' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return `<span class="${classes}" aria-label="Step ${index + 1}: ${step}"></span>`;
  }).join('');
}

/** @param {GiftingSession} session */
function renderIntro(session) {
  const root = document.querySelector('[data-gifting-root]');
  const firstName = formState.firstName || splitName(session.influencer?.name).firstName || 'there';
  const campaignName = session.campaign?.name || 'your gifting campaign';
  const min = Number(session.campaign?.minItemAllowance || 0);
  const max = Number(session.campaign?.maxItemAllowance || 0);
  let lead = 'Select your pieces below, then complete your details.';

  if (min > 0 && max > 0 && min !== max) {
    lead = `Pick ${min} to ${max} items below, then complete your details.`;
  } else if (max > 0) {
    lead = `Pick up to ${max} item${max === 1 ? '' : 's'} below, then complete your details.`;
  } else if (min > 0) {
    lead = `Pick at least ${min} item${min === 1 ? '' : 's'} below, then complete your details.`;
  }

  return `
    <div class="gifting-ambassador__intro">
      <p class="gifting-ambassador__programme">${escapeHtml(root instanceof HTMLElement ? root.dataset.programmeLabel || 'Gifting programme' : 'Gifting programme')}</p>
      <h1 class="gifting-ambassador__greeting">Hi ${escapeHtml(firstName)}, welcome to ${escapeHtml(campaignName)}</h1>
      <p class="gifting-ambassador__lead">${lead}</p>
    </div>
  `;
}

/** @param {GiftingProduct} product @param {GiftingSession} session */
function renderProductCard(product, session) {
  const outOfStock = isOutOfStockBadge(product.stockBadge) || product.isAvailable === false;
  const availableSizes = (product.sizes || []).filter((size) => Number(size.stockAvailable) > 0);
  const disabled = outOfStock || availableSizes.length === 0;
  const productKey = getProductKey(product);
  const selection = selections[productKey];
  const isSelected = isActiveSelection(selection);
  const lowStock = isLowStockBadge(product.stockBadge);
  const shipFrom = getProductShipFromDisplay(product, session);

  const sizeChips = availableSizes
    .map((size) => {
      const selected = Number(selection?.sizeId) === Number(size.sizeId);
      return `
        <button
          type="button"
          class="gifting-ambassador__size-chip${selected ? ' is-selected' : ''}"
          data-line-id="${product.lineId}"
          data-size-id="${size.sizeId}"
          ${disabled || isReadOnly ? 'disabled' : ''}
        >${escapeHtml(size.name)}</button>
      `;
    })
    .join('');

  return `
    <article class="gifting-ambassador__product-card${isSelected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}" data-line-id="${product.lineId}">
      <div class="gifting-ambassador__product-media">
        ${product.thumbUrl ? `<img src="${escapeHtml(product.thumbUrl)}" alt="${escapeHtml(product.name)}" loading="lazy" decoding="async">` : ''}
      </div>
      <div class="gifting-ambassador__product-info">
        <p class="gifting-ambassador__product-name">${escapeHtml(product.name)}</p>
        ${product.dropName ? `<p class="gifting-ambassador__product-drop">${escapeHtml(product.dropName)}</p>` : ''}
        ${
          shipFrom
            ? `<p class="gifting-ambassador__product-ship"><span class="gifting-ambassador__product-ship-label">Ships from</span> ${escapeHtml(shipFrom)}</p>`
            : ''
        }
        ${product.isPendingProduct ? '<span class="gifting-ambassador__stock-badge">Coming soon</span>' : ''}
        ${disabled ? `<span class="gifting-ambassador__stock-badge">${outOfStock ? 'Out of stock' : 'Unavailable'}</span>` : `<div class="gifting-ambassador__size-row">${sizeChips}</div>`}
        ${lowStock && !disabled ? '<span class="gifting-ambassador__stock-badge">Low stock</span>' : ''}
        ${isSelected ? '<div class="gifting-ambassador__added-badge">✓ Added</div>' : ''}
      </div>
    </article>
  `;
}

/** @param {GiftingSession} session */
function renderProductsStep(session) {
  const container = document.querySelector('[data-gifting-step="products"]');
  if (!(container instanceof HTMLElement)) return;

  const products = [...(session.products || [])];
  const dropGroups = groupProductsByDrop(products, session);
  ensureProductListUiState(dropGroups);

  const heroAsset =
    [...(session.assets || [])]
      .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0))
      .find((asset) => asset.isHero && String(asset.assetType).toLowerCase() === 'image') ||
    session.assets?.find((asset) => String(asset.assetType).toLowerCase() === 'image');

  const heroUrl = heroAsset ? getAssetUrl(heroAsset) : '';
  const briefHtml = String(session.campaign?.briefHtml || '').trim();
  const productCatalog = dropGroups.length
    ? `<div class="gifting-ambassador__drop-list" data-gifting-drop-list>${dropGroups.map((group) => renderDropSection(group, session)).join('')}</div>`
    : '';

  container.innerHTML = `
    ${renderIntro(session)}
    ${heroUrl ? `<div class="gifting-ambassador__hero"><img src="${escapeHtml(heroUrl)}" alt="${escapeHtml(heroAsset?.title || session.campaign?.name || 'Campaign')}" decoding="async"></div>` : ''}
    ${briefHtml ? `<div class="gifting-ambassador__brief rte">${sanitizeHtml(briefHtml)}</div>` : ''}
    ${renderSelectionCountHtml(session)}
    ${renderSelectedSummaryHtml(session)}
    <p class="gifting-ambassador__section-label">Select your pieces <span class="req">*</span></p>
    ${productCatalog}
    <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
      <button type="button" class="gifting-ambassador__btn-primary" data-gifting-next>Continue to details →</button>
    </div>
  `;
}

/** @param {GiftingSession} session */
function renderDetailsStep(session) {
  const container = document.querySelector('[data-gifting-step="details"]');
  if (!(container instanceof HTMLElement)) return;

  const deliverables = getDeliverables(session);
  const deliverableRows = deliverables.length
    ? deliverables
        .map((item) => {
          const checked = formState.deliverableIds.includes(item.id);
          return `
            <label class="gifting-ambassador__check-row${checked ? ' is-checked' : ''}" data-deliverable-id="${item.id}">
              <span class="gifting-ambassador__check-box${checked ? ' is-checked' : ''}">${checked ? '✓' : ''}</span>
              <span>${escapeHtml(item.label)}${item.required ? ' <span class="req">*</span>' : ''}</span>
            </label>
          `;
        })
        .join('')
    : '';

  const igHandleRequired = isCodeAccessMode() || (isTokenAccessMode() && !igHandleLocked);
  const igHandleReadonly = isReadOnly || igHandleLocked;

  container.innerHTML = `
    <h2 class="gifting-ambassador__form-section-title">Your details</h2>
    <div class="gifting-ambassador__form-grid">
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label req" for="gifting-first-name">First name</label>
        <input class="gifting-ambassador__input" id="gifting-first-name" data-field="firstName" value="${escapeHtml(formState.firstName)}" ${isReadOnly ? 'readonly' : ''}>
      </div>
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label req" for="gifting-last-name">Last name</label>
        <input class="gifting-ambassador__input" id="gifting-last-name" data-field="lastName" value="${escapeHtml(formState.lastName)}" ${isReadOnly ? 'readonly' : ''}>
      </div>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-email">Email</label>
      <input class="gifting-ambassador__input" id="gifting-email" type="email" data-field="email" value="${escapeHtml(formState.email)}" ${isReadOnly ? 'readonly' : ''}>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label${igHandleRequired ? ' req' : ''}" for="gifting-ig-handle">Instagram or TikTok handle</label>
      <input class="gifting-ambassador__input" id="gifting-ig-handle" type="text" data-field="igHandle" autocapitalize="off" autocomplete="username" placeholder="@instagram or @tiktok" value="${escapeHtml(formState.igHandle)}" ${igHandleReadonly ? 'readonly' : ''}>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-phone">Phone number</label>
      <input class="gifting-ambassador__input" id="gifting-phone" type="tel" data-field="phone" value="${escapeHtml(formState.phone)}" ${isReadOnly ? 'readonly' : ''}>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-address">Shipping address</label>
      <input class="gifting-ambassador__input" id="gifting-address" data-field="addressLine1" value="${escapeHtml(formState.addressLine1)}" ${isReadOnly ? 'readonly' : ''}>
    </div>
    <div class="gifting-ambassador__form-grid">
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label" for="gifting-city">City</label>
        <input class="gifting-ambassador__input" id="gifting-city" data-field="city" value="${escapeHtml(formState.city)}" ${isReadOnly ? 'readonly' : ''}>
      </div>
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label req" for="gifting-postcode">Postcode</label>
        <input class="gifting-ambassador__input" id="gifting-postcode" data-field="postcode" value="${escapeHtml(formState.postcode)}" ${isReadOnly ? 'readonly' : ''}>
      </div>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-country">Country</label>
      <input class="gifting-ambassador__input" id="gifting-country" data-field="country" value="${escapeHtml(formState.country)}" ${isReadOnly ? 'readonly' : ''}>
    </div>
    ${
      deliverableRows
        ? `
          <div class="gifting-ambassador__form-section">
            <h3 class="gifting-ambassador__form-section-title">Content deliverables <span class="req">*</span></h3>
            <p class="gifting-ambassador__form-section-lead">Please confirm which content deliverables you would like to create</p>
            ${deliverableRows}
          </div>
        `
        : ''
    }
    <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
      <button type="button" class="gifting-ambassador__btn-primary" data-gifting-next>Continue to terms →</button>
      <button type="button" class="gifting-ambassador__btn-secondary" data-gifting-back>← Back to product picks</button>
    </div>
  `;
}

/** @param {GiftingSession} session */
function renderTermsStep(session) {
  const container = document.querySelector('[data-gifting-step="terms"]');
  if (!(container instanceof HTMLElement)) return;

  const deadline = session.campaign?.contentDeadline || '';
  const deadlineNote = session.campaign?.contentDeadlineNote || '';
  const agreementHtml = String(session.campaign?.agreementHtml || session.campaign?.termsHtml || '').trim();
  const confirmChecked = formState.deadlineChoice === 'confirm';
  const needTimeChecked = formState.deadlineChoice === 'need_time';

  container.innerHTML = `
    <h2 class="gifting-ambassador__form-section-title">Content deadline + terms</h2>
    ${renderSubmitSummaryHtml(session)}
    ${
      deadline
        ? `
          <div class="gifting-ambassador__deadline-box">
            <p class="gifting-ambassador__deadline-title">Content due ${escapeHtml(deadline)}</p>
            <p class="gifting-ambassador__deadline-sub">${escapeHtml(deadlineNote || 'We require content posted by this date. Let us know if you need additional time.')}</p>
          </div>
          <p class="gifting-ambassador__field-label req">Can you meet this deadline?</p>
          <button type="button" class="gifting-ambassador__radio-row${confirmChecked ? ' is-checked' : ''}" data-deadline-choice="confirm">
            <span class="gifting-ambassador__radio-dot${confirmChecked ? ' is-checked' : ''}">${confirmChecked ? '<span class="gifting-ambassador__radio-inner"></span>' : ''}</span>
            <span>I confirm I can post my content on ${escapeHtml(deadline)} as part of the campaign launch.</span>
          </button>
          <button type="button" class="gifting-ambassador__radio-row${needTimeChecked ? ' is-checked' : ''}" data-deadline-choice="need_time">
            <span class="gifting-ambassador__radio-dot${needTimeChecked ? ' is-checked' : ''}">${needTimeChecked ? '<span class="gifting-ambassador__radio-inner"></span>' : ''}</span>
            <span>I require additional time — I will send a proposed date via DM or email.</span>
          </button>
        `
        : ''
    }
    <div class="gifting-ambassador__form-section">
      <label class="gifting-ambassador__field-label" for="gifting-notes">Notes (optional)</label>
      <textarea class="gifting-ambassador__textarea" id="gifting-notes" data-field="notes" placeholder="Ivory colourway if possible…">${escapeHtml(formState.notes)}</textarea>
    </div>
    <div class="gifting-ambassador__form-section">
      <h3 class="gifting-ambassador__form-section-title">Agreement &amp; submission</h3>
      ${
        agreementHtml
          ? `<div class="gifting-ambassador__tc-box">${sanitizeHtml(agreementHtml)}</div>`
          : `
            <div class="gifting-ambassador__tc-box">
              <p>By submitting this form you agree to:</p>
              <ul>
                <li>Deliver your selected deliverables by the agreed date</li>
                <li>Post content on the agreed date</li>
                <li>Grant SABO full usage rights to your content</li>
              </ul>
              <p>We're so excited to collaborate and can't wait to see your creative direction!</p>
            </div>
          `
      }
      <label class="gifting-ambassador__consent-row${formState.termsAccepted ? ' is-checked' : ''}">
        <span class="gifting-ambassador__check-box${formState.termsAccepted ? ' is-checked' : ''}" data-consent="terms">${formState.termsAccepted ? '✓' : ''}</span>
        <span>I agree to the terms and deliverables of this collaboration.</span>
      </label>
      <label class="gifting-ambassador__consent-row${formState.dataConsentAccepted ? ' is-checked' : ''}">
        <span class="gifting-ambassador__check-box${formState.dataConsentAccepted ? ' is-checked' : ''}" data-consent="data">${formState.dataConsentAccepted ? '✓' : ''}</span>
        <span>I consent to SABO Skirt storing my personal details and shipping address for fulfilment purposes.</span>
      </label>
    </div>
    <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
      <button type="button" class="gifting-ambassador__btn-primary" data-gifting-submit ${session.canSubmit ? '' : 'disabled'}>Submit preferences</button>
      <button type="button" class="gifting-ambassador__btn-secondary" data-gifting-back>← Back to details</button>
    </div>
  `;
}

/** @param {GiftingSession} session */
function showStep(session) {
  const steps = getActiveSteps(session);

  steps.forEach((step, index) => {
    const el = document.querySelector(`[data-gifting-step="${step}"]`);
    if (el instanceof HTMLElement) {
      el.hidden = index !== currentStepIndex;
    }
  });

  ['products', 'details', 'terms'].forEach((step) => {
    if (steps.includes(step)) return;
    const el = document.querySelector(`[data-gifting-step="${step}"]`);
    if (el instanceof HTMLElement) el.hidden = true;
  });

  renderProgress(session);

  const step = steps[currentStepIndex];
  if (step === 'products') renderProductsStep(session);
  if (step === 'details') renderDetailsStep(session);
  if (step === 'terms') renderTermsStep(session);

  bindStepEvents(session);
}

/** @param {GiftingSession} session */
function bindStepEvents(session) {
  const steps = getActiveSteps(session);
  const activeStep = document.querySelector(`[data-gifting-step="${steps[currentStepIndex]}"]`);
  if (!(activeStep instanceof HTMLElement)) return;

  activeStep.querySelectorAll('[data-field]').forEach((input) => {
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    if (input.dataset.bound) return;
    input.dataset.bound = 'true';
    input.addEventListener('input', () => {
      const field = input.dataset.field;
      if (!field) return;
      formState[field] = input.value;
    });
  });

  const productStep = document.querySelector('[data-gifting-step="products"]');
  if (productStep instanceof HTMLElement && productStep.dataset.eventsBound !== 'true') {
    productStep.dataset.eventsBound = 'true';
    productStep.addEventListener('click', (event) => {
      const session = sessionData;
      if (!session) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const loadMore = target.closest('[data-gifting-load-more]');
      if (loadMore instanceof HTMLButtonElement) {
        const dropKey = loadMore.dataset.dropKey || '';
        if (!dropKey) return;
        visibleCountByDrop[dropKey] =
          (visibleCountByDrop[dropKey] ?? GIFTING_PRODUCTS_PAGE_SIZE) + GIFTING_PRODUCTS_PAGE_SIZE;
        renderProductsStep(session);
        bindStepEvents(session);
        return;
      }

      const dropToggle = target.closest('[data-gifting-drop-toggle]');
      if (dropToggle instanceof HTMLButtonElement) {
        const dropKey = dropToggle.dataset.dropKey || '';
        if (!dropKey) return;
        collapsedDrops[dropKey] = !collapsedDrops[dropKey];
        const section = dropToggle.closest('.gifting-ambassador__drop');
        if (section instanceof HTMLElement) {
          const collapsed = Boolean(collapsedDrops[dropKey]);
          section.classList.toggle('is-collapsed', collapsed);
          dropToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        }
        return;
      }

      const chip = target.closest('[data-size-id]');
      if (!(chip instanceof HTMLButtonElement) || isReadOnly) return;

      const lineId = Number(chip.dataset.lineId);
      const sizeId = Number(chip.dataset.sizeId);
      const product = session.products.find((item) => item.lineId === lineId);
      if (!product) return;

      const max = Number(session.campaign.maxItemAllowance) || 0;
      const productKey = getProductKey(product);
      const existing = selections[productKey];
      const currentlySelected = isActiveSelection(existing);

      if (!currentlySelected && max > 0 && getSelectedCount() >= max) {
        setStepError('You have reached the maximum number of items for this campaign.');
        return;
      }

      if (currentlySelected && Number(existing?.sizeId) === sizeId) {
        delete selections[productKey];
      } else {
        selections[productKey] = {
          lineId: product.lineId,
          productId: product.productId,
          scheduleItemId: product.scheduleItemId,
          isPendingProduct: product.isPendingProduct,
          sizeId,
          qty: 1,
        };
      }

      setStepError('');
      renderProductsStep(session);
      bindStepEvents(session);
    });
  }

  activeStep.querySelectorAll('[data-deliverable-id]').forEach((row) => {
    if (!(row instanceof HTMLElement) || row.dataset.bound) return;
    row.dataset.bound = 'true';
    row.addEventListener('click', () => {
      const id = Number(row.getAttribute('data-deliverable-id'));
      if (!id) return;

      if (formState.deliverableIds.includes(id)) {
        formState.deliverableIds = formState.deliverableIds.filter((value) => value !== id);
      } else {
        formState.deliverableIds = [...formState.deliverableIds, id];
      }

      renderDetailsStep(session);
      bindStepEvents(session);
    });
  });

  activeStep.querySelectorAll('[data-deadline-choice]').forEach((row) => {
    if (!(row instanceof HTMLButtonElement) || row.dataset.bound) return;
    row.dataset.bound = 'true';
    row.addEventListener('click', () => {
      formState.deadlineChoice = row.dataset.deadlineChoice === 'need_time' ? 'need_time' : 'confirm';
      renderTermsStep(session);
      bindStepEvents(session);
    });
  });

  activeStep.querySelectorAll('[data-consent]').forEach((box) => {
    const row = box.closest('.gifting-ambassador__consent-row');
    if (!(row instanceof HTMLElement) || row.dataset.bound) return;
    row.dataset.bound = 'true';
    row.addEventListener('click', () => {
      const type = box.getAttribute('data-consent');
      if (type === 'terms') formState.termsAccepted = !formState.termsAccepted;
      if (type === 'data') formState.dataConsentAccepted = !formState.dataConsentAccepted;
      renderTermsStep(session);
      bindStepEvents(session);
    });
  });

  const nextBtn = activeStep.querySelector('[data-gifting-next]');
  if (nextBtn instanceof HTMLButtonElement && !nextBtn.dataset.bound) {
    nextBtn.dataset.bound = 'true';
    nextBtn.addEventListener('click', () => {
      if (!validateStep(session)) return;
      currentStepIndex = Math.min(currentStepIndex + 1, getActiveSteps(session).length - 1);
      showStep(session);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  const backBtn = activeStep.querySelector('[data-gifting-back]');
  if (backBtn instanceof HTMLButtonElement && !backBtn.dataset.bound) {
    backBtn.dataset.bound = 'true';
    backBtn.addEventListener('click', () => {
      syncFieldsFromDom(activeStep);
      currentStepIndex = Math.max(0, currentStepIndex - 1);
      showStep(session);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  const submitBtn = activeStep.querySelector('[data-gifting-submit]');
  if (submitBtn instanceof HTMLButtonElement && !submitBtn.dataset.bound) {
    submitBtn.dataset.bound = 'true';
    submitBtn.addEventListener('click', () => submitPreferences(session));
  }
}

/** @param {HTMLElement} scope */
function syncFieldsFromDom(scope) {
  scope.querySelectorAll('[data-field]').forEach((input) => {
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    const field = input.dataset.field;
    if (!field) return;
    formState[field] = input.value;
  });
}

/** @param {string} message */
function setStepError(message) {
  const steps = getActiveSteps(sessionData);
  const activeStep = document.querySelector(`[data-gifting-step="${steps[currentStepIndex]}"]`);
  const errorEl = activeStep?.querySelector('[data-gifting-step-error]');
  if (!(errorEl instanceof HTMLElement)) return;

  if (!message) {
    errorEl.hidden = true;
    errorEl.textContent = '';
    return;
  }

  errorEl.hidden = false;
  errorEl.textContent = message;
}

/** @param {GiftingSession} session */
function validateStep(session) {
  const steps = getActiveSteps(session);
  const step = steps[currentStepIndex];

  if (step === 'products') {
    const count = getSelectedCount();
    const min = Number(session.campaign.minItemAllowance || 0);
    const max = Number(session.campaign.maxItemAllowance || 0);

    if (count === 0) {
      setStepError('Please select at least one piece and size to continue.');
      return false;
    }

    if (min > 0 && count < min) {
      setStepError(`Please select at least ${min} item${min === 1 ? '' : 's'}.`);
      return false;
    }

    if (max > 0 && count > max) {
      setStepError(`You can select up to ${max} items.`);
      return false;
    }

    setStepError('');
    return true;
  }

  if (step === 'details') {
    const activeStep = document.querySelector('[data-gifting-step="details"]');
    if (activeStep instanceof HTMLElement) syncFieldsFromDom(activeStep);

    const required = [
      ['firstName', 'First name'],
      ['lastName', 'Last name'],
      ['email', 'Email'],
      ['phone', 'Phone number'],
      ['addressLine1', 'Shipping address'],
      ['postcode', 'Postcode'],
      ['country', 'Country'],
    ];

    if (isCodeAccessMode() || (isTokenAccessMode() && !igHandleLocked)) {
      required.splice(3, 0, ['igHandle', 'Instagram or TikTok handle']);
    }

    for (const [field, label] of required) {
      if (!String(formState[field] || '').trim()) {
        setStepError(`${label} is required.`);
        return false;
      }
    }

    const deliverables = getDeliverables(session);
    if (deliverables.length && formState.deliverableIds.length === 0) {
      setStepError('Please select at least one content deliverable.');
      return false;
    }

    const missingRequired = deliverables
      .filter((item) => item.required)
      .filter((item) => !formState.deliverableIds.includes(item.id));

    if (missingRequired.length) {
      setStepError(`Please select: ${missingRequired.map((item) => item.label).join(', ')}.`);
      return false;
    }

    setStepError('');
    return true;
  }

  if (step === 'terms') {
    const activeStep = document.querySelector('[data-gifting-step="terms"]');
    if (activeStep instanceof HTMLElement) syncFieldsFromDom(activeStep);

    if (session.campaign.contentDeadline && !formState.deadlineChoice) {
      setStepError('Please confirm whether you can meet the content deadline.');
      return false;
    }

    if (!formState.termsAccepted) {
      setStepError('Please agree to the collaboration terms.');
      return false;
    }

    if (!formState.dataConsentAccepted) {
      setStepError('Please consent to storing your details for fulfilment.');
      return false;
    }

    setStepError('');
    return true;
  }

  return true;
}

/** @param {GiftingSession} session */
function buildSubmitPayload() {
  const lines = Object.values(selections)
    .filter((entry) => isActiveSelection(entry))
    .map((entry) => {
      if (entry.isPendingProduct || entry.productId <= 0) {
        return {
          ProductId: 0,
          ScheduleItemId: entry.scheduleItemId,
          SizeId: entry.sizeId,
          Qty: entry.qty,
        };
      }

      return {
        ProductId: entry.productId,
        ScheduleItemId: 0,
        SizeId: entry.sizeId,
        Qty: entry.qty,
      };
    });

  let contentDeadlineResponse = '';

  if (formState.deadlineChoice === 'confirm') {
    contentDeadlineResponse = 'Confirm';
  } else if (formState.deadlineChoice === 'need_time') {
    contentDeadlineResponse = 'NeedsExtension';
  }

  /** @type {Record<string, unknown>} */
  const payload = {
    Notes: formState.notes.trim(),
    Lines: lines,
    ContentDeadlineResponse: contentDeadlineResponse,
    TermsAccepted: formState.termsAccepted,
    DataConsentAccepted: formState.dataConsentAccepted,
    SelectedDeliverableIds: formState.deliverableIds,
  };

  if (inviteToken) {
    payload.Token = inviteToken;
  } else if (campaignCode) {
    payload.CampaignCode = campaignCode;
  }

  if (isCodeAccessMode() || isTokenAccessMode() || sessionData?.requiresIdentity) {
    payload.FirstName = formState.firstName.trim();
    payload.LastName = formState.lastName.trim();
    payload.Email = formState.email.trim();
    payload.Phone = formState.phone.trim();
    payload.AddressLine1 = formState.addressLine1.trim();
    payload.City = formState.city.trim();
    payload.Postcode = formState.postcode.trim();
    payload.Country = formState.country.trim();

    const igHandle = formState.igHandle.trim();
    if (igHandle) {
      payload.IgHandle = igHandle;
    }
  }

  return payload;
}

/** @param {GiftingSession} session */
async function submitPreferences(session) {
  if (!validateStep(session)) return;

  const submitBtn = document.querySelector('[data-gifting-submit]');
  if (!(submitBtn instanceof HTMLButtonElement) || submitBtn.disabled) return;

  submitBtn.setAttribute('aria-busy', 'true');
  submitBtn.disabled = true;

  try {
    const submitQuery = inviteToken ? `?token=${encodeURIComponent(inviteToken)}` : '';
    const response = await fetch(`${getProxyBase()}/submit${submitQuery}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify(buildSubmitPayload()),
    });

    if (response.status === 401) {
      throw new Error('Unable to submit preferences. Please open the link from your email.');
    }

    let data = null;

    try {
      data = await response.json();
    } catch (_error) {
      if (response.status === 404) {
        throw new Error('Submit is not available yet. Please check back soon.');
      }
    }

    if (response.status === 404) {
      throw new Error('Submit is not available yet. Please check back soon.');
    }

    const success = Boolean(pick(data, 'Success', 'success'));
    const message = String(pick(data, 'Message', 'message') || '');

    if (!response.ok || !success) {
      throw new Error(message || 'Unable to submit preferences. Please review your selections and try again.');
    }

    clearStoredCampaignCode();
    showSubmitted(session, message);
  } catch (error) {
    setStepError(error instanceof Error ? error.message : 'Unable to submit preferences.');
    submitBtn.removeAttribute('aria-busy');
    submitBtn.disabled = !session.canSubmit;
  }
}

/** @param {GiftingSession} session */
function renderSession(session) {
  sessionData = session;
  initFormState(session);

  if (applyReadOnlyStatus(session)) return;

  hideViews(['wizard']);

  currentStepIndex = 0;
  showStep(session);
}

/** @param {string} token @param {{ fromUnlock?: boolean }} [options] */
async function loadInviteByToken(token, options = {}) {
  accessMode = 'token';
  inviteToken = String(token || '').trim();
  clearStoredCampaignCode();

  if (!inviteToken) {
    showUnlock();
    if (options.fromUnlock) {
      setUnlockError('Please enter your campaign code.');
    }
    return;
  }

  if (!options.fromUnlock) {
    showLoading();
  }

  try {
    const data = await fetchSessionByToken(inviteToken);
    handleSessionPayload(data, {
      fromUnlock: options.fromUnlock,
      invalidMessage: 'That invite link is not valid. Check your email and try again.',
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unable to load invite.';

    if (options.fromUnlock) {
      showUnlock();
      setUnlockError(errorMessage);
      return;
    }

    showError(errorMessage, 'Unable to load invite');
  }
}

/** @param {string} code @param {{ fromUnlock?: boolean }} [options] */
async function unlockByCampaignCode(code, options = {}) {
  accessMode = 'code';
  inviteToken = '';
  persistCampaignCode(code);

  if (!campaignCode) {
    showUnlock();
    setUnlockError('Please enter your campaign code.');
    return;
  }

  if (!options.fromUnlock) {
    showLoading();
  }

  try {
    const data = await fetchSessionByCampaignCode(campaignCode);
    handleSessionPayload(data, { fromUnlock: options.fromUnlock });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unable to unlock campaign.';

    if (options.fromUnlock) {
      showUnlock();
      setUnlockError(errorMessage);
      return;
    }

    showError(errorMessage, 'Unable to unlock campaign');
  }
}

function stripCampaignCodeFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('code')) return;

  url.searchParams.delete('code');
  history.replaceState({}, '', url);
}

async function init() {
  if (window.SaboGiftingReCollabMode) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const answer = (params.get('answer') || '').trim().toLowerCase();
  if (answer === 'yes' || answer === 'no') {
    return;
  }

  stripCampaignCodeFromUrl();
  inviteToken = resolveInviteToken();

  if (inviteToken) {
    await loadInviteByToken(inviteToken);
    return;
  }

  const previewCode = resolvePreviewCampaignCode();
  if (previewCode) {
    await unlockByCampaignCode(previewCode);
    return;
  }

  const storedCode = readStoredCampaignCode();
  if (storedCode) {
    await unlockByCampaignCode(storedCode);
    return;
  }

  showUnlock();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
